'use client';

import { X, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MemoCard } from './MemoCard';
import type { Memo } from '../momento/MomentoContext';

interface MemosPreviewGridProps {
  memos: Memo[];
  isLoading?: boolean;
  onSelectMemo: (memo: Memo) => void;
  onClose: () => void;
  onNewMemo?: () => void;
}

export function MemosPreviewGrid({
  memos,
  isLoading = false,
  onSelectMemo,
  onClose,
  onNewMemo,
}: MemosPreviewGridProps) {
  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-momento-900 dark:text-momento-50">
            Recent Memos
          </h3>
          {memos.length > 0 && (
            <span className="text-xs text-momento-500">
              {memos.length} {memos.length === 1 ? 'memo' : 'memos'}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-7 w-7 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Memos Grid */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-3">
            {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-momento-400" />
              <p className="text-sm text-momento-500">Loading memos...</p>
            </div>
          ) : memos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-16 h-16 rounded-full bg-momento-100 dark:bg-momento-800 flex items-center justify-center">
                <svg className="w-8 h-8 text-momento-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-momento-700 dark:text-momento-300">
                  No memos yet
                </p>
                <p className="text-xs text-momento-500 mt-1">
                  Create your first memo by asking me<br />
                  to edit a moment from your videos
                </p>
              </div>
            </div>
          ) : (
            memos.map((memo) => (
              <MemoCard
                key={memo.id}
                memo={memo}
                onClick={() => onSelectMemo(memo)}
              />
            ))
          )}
          </div>
        </ScrollArea>
      </div>

      {/* Footer */}
      {onNewMemo && (
        <div className="px-4 py-3 border-t flex-shrink-0">
          <Button
            variant="outline"
            className="w-full"
            onClick={onNewMemo}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Memo
          </Button>
        </div>
      )}
    </div>
  );
}
